import { EmfitStore } from './EmfitStore';
import { DevicesStore } from './DevicesStore';
import { GroupsStore } from './GroupsStore';
import { UsersStore } from './UsersStore';
import { UserStore } from './UserStore';
import { EventLogStore } from './EventLogStore';
import { AuditLogStore } from './AuditLogStore';
import { GroupDashboardStore } from './GroupDashboardStore';
import { PushTokenStore } from './PushTokenStore';
import { TwoFactorStore } from './TwoFactorStore';
import { Wire } from '../services/Wire';

interface Resettable {
  reset(): void;
}

/**
 * Stores holding data that belongs to one login. Logout navigates with the
 * router rather than reloading the page, so these singletons outlive the
 * session and the next login would otherwise read the previous user's devices.
 *
 * Built on call, not at module load: EmfitStore imports SessionStore and
 * SessionStore imports this module, so at load time that binding is still
 * undefined.
 */
export function resettableStores(): readonly Resettable[] {
  return [
    EmfitStore,
    DevicesStore,
    GroupsStore,
    UsersStore,
    UserStore,
    EventLogStore,
    AuditLogStore,
    GroupDashboardStore,
    PushTokenStore,
    TwoFactorStore,
  ];
}

/**
 * Stores deliberately left alone, by their export name in ./index:
 * - SessionStore owns the logout itself and clears its own token.
 * - PreferencesStore is reset separately by logout; it also holds choices that
 *   survive a login, such as the locale.
 * - RegistrationStore belongs to account creation, before any login exists.
 * - DeviceStore is a class, not a singleton.
 */
export const STORES_KEPT_ACROSS_SESSIONS: readonly string[] = [
  'SessionStore',
  'PreferencesStore',
  'RegistrationStore',
  'DeviceStore',
];

/**
 * Empties every store belonging to the login that is ending, and the real-time
 * status Wire holds alongside them.
 */
export function resetStores(): void {
  for (const store of resettableStores()) {
    store.reset();
  }
  Wire.reset();
}
